import React, { type ChangeEvent, useRef, useState } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { createFileUrl, uploadFile } from 'common/files/files.service'
import { setErrorMessage } from 'store/app.slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import { ReactComponent as PlusIcon } from 'assets/plusIcon.svg'
import { useDidMountEffect } from 'common/utils/hooks'

import styles from './FileUploader.module.scss'

const getFileArray = (fileList: FileList): File[] => {
  const list: File[] = []
  for (let i = 0; i < fileList.length; i++) {
    list.push(fileList[i])
  }
  return list
}

interface FileUploaderProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  onFileChange: (files: Array<FileType | GeneratedFile>, multiple: boolean) => void
  multiple: boolean
  fileType: GeneratedFileType
  placeholder?: string
  accept?: string
  proposalId?: string
  error?: boolean
}

export interface FileType {
  key: string
  filename: string
}
type GetDataReturnType =
  | { filename: string; vendorId?: string }
  | { filename: string; proposalId?: string }
  | { filename: string }

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileChange,
  multiple,
  fileType,
  placeholder,
  accept = '.doc,.docx,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,.pages,application/vnd.apple.pages',
  proposalId,
  error,
  ...rest
}) => {
  const dispatch = useAppDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState<GeneratedFile[]>([])
  const { vendor } = useAppSelector((state) => state.vendor)

  const getData = (file: File): GetDataReturnType => {
    switch (fileType) {
      case GeneratedFileType.VendorRefDoc:
        return { filename: file.name, vendorId: vendor?.id }
      case GeneratedFileType.AIVendorRefDoc:
        return { filename: file.name, vendorId: vendor?.id }
      case GeneratedFileType.ProposalDoc:
        return { filename: file.name, vendorId: vendor?.id, proposalId }
      default:
        return { filename: file.name }
    }
  }
  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    ;(async () => {
      setLoading(true)
      const uploadedFiles = event.target.files ? getFileArray(event.target.files) : []
      try {
        const fileData = await Promise.all(
          uploadedFiles.map(async (file) => {
            const generatedFile = await createFileUrl({
              type: fileType,
              mode: 'rw',
              multiple,
              data: getData(file),
            })
            await uploadFile(generatedFile, file)
            return generatedFile
          }),
        )
        if (multiple) {
          setFiles((prevFiles) => [...prevFiles, ...fileData])
        } else {
          setFiles(fileData)
        }
      } catch (e) {
        console.error('error uploading files: ', e)
        dispatch(setErrorMessage('Error uploading files'))
      } finally {
        event.target.value = ''
        setLoading(false)
      }
    })().catch((error) => {
      // Handle or log the error here
      console.error('An error occurred:', error)
    })
  }

  useDidMountEffect(() => {
    onFileChange(files, multiple)
  }, [files])

  const handlePlusIconClick = (): void => {
    fileInputRef?.current?.click()
  }

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        disabled={loading}
        {...rest}
      />
      <div
        className={`${styles.addItemButton} ${error ? styles.error : ''} ${
          placeholder ? styles.placeholder : ''
        }`}
        onClick={handlePlusIconClick}
      >
        {placeholder && <Typography variant="subtitle1">{placeholder}</Typography>}
        <div className={styles.iconWrapper}>{!loading ? <PlusIcon /> : <CircularProgress size={16} />}</div>
      </div>
    </div>
  )
}

export default FileUploader
